import React, { useState, useEffect } from 'react';
import { MessageSquare, Mail, User, Clock, ChevronDown, ChevronUp, Lock, Users, Plus, Trash2, Pencil, Globe, Settings } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [token, setToken] = useState(localStorage.getItem('adminToken') || null);
  const [currentUser, setCurrentUser] = useState(() => {
    const saved = localStorage.getItem('adminUser');
    return saved ? JSON.parse(saved) : null;
  });

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  
  const [activeTab, setActiveTab] = useState('chats'); // 'chats' | 'users' | 'settings' (settings only for tenant admin)
  
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedSessions, setExpandedSessions] = useState({});

  // Super Admin Chat Filtering
  const [selectedWebsiteFilter, setSelectedWebsiteFilter] = useState('all');

  // Users Management State
  const [users, setUsers] = useState([]);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newWebsiteId, setNewWebsiteId] = useState('');
  const [newAllowedDomain, setNewAllowedDomain] = useState('');
  const [newCustomPrompt, setNewCustomPrompt] = useState('');
  const [userError, setUserError] = useState('');

  // Edit user modal state
  const [editUser, setEditUser] = useState(null);
  const [editUsername, setEditUsername] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [editWebsiteId, setEditWebsiteId] = useState('');
  const [editAllowedDomain, setEditAllowedDomain] = useState('');
  const [editCustomPrompt, setEditCustomPrompt] = useState('');
  const [editUserError, setEditUserError] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  // Tenant admin: bot settings (visible in Settings tab)
  const [myPrompt, setMyPrompt] = useState('');
  const [myBotColor, setMyBotColor] = useState('#2563eb');
  const [myBotName, setMyBotName] = useState('Direct Media');
  const [myWelcomeTitle, setMyWelcomeTitle] = useState('مرحباً بك!');
  const [myWelcomeSubtitle, setMyWelcomeSubtitle] = useState('من فضلك ادخل بياناتك للبدء');
  const [myPromptLoading, setMyPromptLoading] = useState(false);
  const [myPromptSaving, setMyPromptSaving] = useState(false);

  const isSuperAdmin = currentUser && currentUser.websiteId === 'all';

  useEffect(() => {
    if (token) {
      if (activeTab === 'chats') {
        fetchSessions();
      } else if (activeTab === 'users' && isSuperAdmin) {
        fetchUsers();
      } else if (activeTab === 'settings' && !isSuperAdmin) {
        fetchMyProfile();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run only when tab/auth change
  }, [token, activeTab, isSuperAdmin]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { username, password });
      const { access_token, username: resUsername, website_id } = response.data;
      
      setToken(access_token);
      const userObj = { username: resUsername, websiteId: website_id };
      setCurrentUser(userObj);
      
      localStorage.setItem('adminToken', access_token);
      localStorage.setItem('adminUser', JSON.stringify(userObj));
    } catch (error) {
      setLoginError('اسم المستخدم أو كلمة المرور غير صحيحة');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setToken(null);
    setCurrentUser(null);
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    setSessions([]);
    setConversations([]);
    setUsers([]);
  };

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(response.data.sessions);
    } catch (error) {
      console.error('خطأ في جلب الجلسات:', error);
      if (error.response && error.response.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchConversations = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/admin/conversations/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data.messages);
      setSelectedSession(sessionId);
    } catch (error) {
      console.error('خطأ في جلب المحادثات:', error);
      if (error.response && error.response.status === 401) {
        handleLogout();
      }
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('خطأ في جلب المستخدمين:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyProfile = async () => {
    try {
      setMyPromptLoading(true);
      const response = await axios.get(`${API}/admin/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyPrompt(response.data.custom_prompt || '');
      setMyBotColor(response.data.bot_color || '#2563eb');
      setMyBotName(response.data.bot_name || 'Direct Media');
      setMyWelcomeTitle(response.data.welcome_title || 'مرحباً بك!');
      setMyWelcomeSubtitle(response.data.welcome_subtitle || 'من فضلك ادخل بياناتك للبدء');
    } catch (error) {
      console.error('خطأ في جلب الملف الشخصي:', error);
      const msg = error.response?.data?.detail || error.message || 'تعذر تحميل الإعدادات';
      const is404 = error.response?.status === 404;
      toast.error(is404 ? 'الرابط غير موجود. تأكد من إعادة تشغيل الـ Backend.' : msg);
    } finally {
      setMyPromptLoading(false);
    }
  };

  const handleSaveMySettings = async (e) => {
    e.preventDefault();
    setMyPromptSaving(true);
    try {
      await axios.put(`${API}/admin/my-settings`, {
        custom_prompt: myPrompt,
        bot_color: myBotColor,
        bot_name: myBotName,
        welcome_title: myWelcomeTitle,
        welcome_subtitle: myWelcomeSubtitle,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('تم حفظ إعدادات البوت بنجاح');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ أثناء الحفظ');
    } finally {
      setMyPromptSaving(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setUserError('');
    if (!newUsername || !newPassword || !newWebsiteId) {
      setUserError('جميع الحقول مطلوبة');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/users`, {
        username: newUsername,
        password: newPassword,
        website_id: newWebsiteId,
        allowed_domain: newAllowedDomain || '',
        custom_prompt: newCustomPrompt.trim() || undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setNewUsername('');
      setNewPassword('');
      setNewWebsiteId('');
      setNewAllowedDomain('');
      setNewCustomPrompt('');
      setUserError('');
      fetchUsers();
      toast.success('تم إنشاء المستخدم بنجاح');
    } catch (error) {
      const msg = error.response?.data?.detail || 'حدث خطأ أثناء إنشاء المستخدم';
      setUserError(msg);
      toast.error(msg);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المستخدم؟')) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchUsers();
      toast.success('تم حذف المستخدم');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ في الحذف');
    }
  };

  const openEditUser = (user) => {
    setEditUser(user);
    setEditUsername(user.username);
    setEditPassword('');
    setEditWebsiteId(user.website_id || '');
    setEditAllowedDomain(user.allowed_domain || '');
    setEditCustomPrompt(user.custom_prompt || '');
    setEditUserError('');
  };

  const closeEditUser = () => {
    setEditUser(null);
    setEditUsername('');
    setEditPassword('');
    setEditWebsiteId('');
    setEditAllowedDomain('');
    setEditCustomPrompt('');
    setEditUserError('');
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setEditUserError('');
    if (!editUser) return;
    if (!editUsername.trim()) {
      setEditUserError('اسم المستخدم مطلوب');
      return;
    }

    setEditLoading(true);
    try {
      const payload = {
        username: editUsername.trim(),
        website_id: editWebsiteId.trim() || undefined,
        allowed_domain: editAllowedDomain.trim() || undefined,
        custom_prompt: editCustomPrompt.trim() || undefined,
      };
      if (editPassword.trim()) payload.password = editPassword;

      await axios.put(`${API}/admin/users/${editUser.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchUsers();
      closeEditUser();
      toast.success('تم حفظ التعديلات بنجاح');
    } catch (error) {
      const msg = error.response?.data?.detail || 'حدث خطأ أثناء التحديث';
      setEditUserError(msg);
      toast.error(msg);
    } finally {
      setEditLoading(false);
    }
  };

  const toggleSession = (sessionId) => {
    if (expandedSessions[sessionId]) {
      setExpandedSessions({ ...expandedSessions, [sessionId]: false });
      if (selectedSession === sessionId) {
        setSelectedSession(null);
        setConversations([]);
      }
    } else {
      setExpandedSessions({ ...expandedSessions, [sessionId]: true });
      fetchConversations(sessionId);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('ar-EG', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" dir="rtl">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full">
          <div className="flex justify-center mb-6">
            <div className="bg-blue-100 p-4 rounded-full">
              <Lock className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">تسجيل الدخول للإدارة</h2>
          
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">اسم المستخدم</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">كلمة المرور</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                required
              />
            </div>

            {loginError && (
              <p className="text-red-500 text-sm text-center">{loginError}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:bg-blue-400"
            >
              {loading ? 'جاري التحقق...' : 'دخول'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Filter sessions based on super admin dropdown
  const filteredSessions = sessions.filter(s => {
    if (!isSuperAdmin && currentUser) return s.website_id === currentUser.websiteId; // Regular admin sees only their website's chats
    if (selectedWebsiteFilter === 'all') return true;
    return s.website_id === selectedWebsiteFilter;
  });

  // Get unique websites for the filter
  const uniqueWebsites = isSuperAdmin ? [...new Set(sessions.map(s => s.website_id))] : [];

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-blue-600 p-2 rounded-lg">
                <MessageSquare className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  {isSuperAdmin ? 'إدارة المنصة (SaaS)' : 'لوحة تحكم المحادثات'}
                </h1>
                <p className="text-xs text-gray-500">مرحباً {currentUser?.username}</p>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab('chats')}
                className={`px-4 py-2 text-sm rounded-lg transition-colors font-medium ${
                  activeTab === 'chats' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <MessageSquare className="w-4 h-4 inline ml-2" />
                المحادثات
              </button>
              {isSuperAdmin && (
                <button
                  onClick={() => setActiveTab('users')}
                  className={`px-4 py-2 text-sm rounded-lg transition-colors font-medium ${
                    activeTab === 'users' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Users className="w-4 h-4 inline ml-2" />
                  العملاء (Tenants)
                </button>
              )}
              {!isSuperAdmin && (
                <button
                  onClick={() => setActiveTab('settings')}
                  className={`px-4 py-2 text-sm rounded-lg transition-colors font-medium ${
                    activeTab === 'settings' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Settings className="w-4 h-4 inline ml-2" />
                  إعدادات البوت
                </button>
              )}
            </div>

            <button
              onClick={handleLogout}
              className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-red-200"
            >
              تسجيل خروج
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {loading && (!sessions.length && !users.length) ? (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">جاري التحميل...</p>
          </div>
        ) : (
          <>
            {activeTab === 'chats' && (
              <>
                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500 font-medium">إجمالي المحادثات</p>
                        <p className="text-3xl font-bold text-gray-900 mt-2">{filteredSessions.length}</p>
                      </div>
                      <div className="bg-blue-50 rounded-xl p-3">
                        <MessageSquare className="w-6 h-6 text-blue-600" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500 font-medium">المحادثات اليوم</p>
                        <p className="text-3xl font-bold text-gray-900 mt-2">
                          {filteredSessions.filter(s => {
                            const today = new Date().toDateString();
                            return new Date(s.started_at).toDateString() === today;
                          }).length}
                        </p>
                      </div>
                      <div className="bg-green-50 rounded-xl p-3">
                        <Clock className="w-6 h-6 text-green-600" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500 font-medium">إجمالي الرسائل المتبادلة</p>
                        <p className="text-3xl font-bold text-gray-900 mt-2">
                          {filteredSessions.reduce((acc, s) => acc + (s.messages_count || 0), 0)}
                        </p>
                      </div>
                      <div className="bg-purple-50 rounded-xl p-3">
                        <Mail className="w-6 h-6 text-purple-600" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sessions List */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                  <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                    <h2 className="text-lg font-bold text-gray-900">المحادثات الأخيرة</h2>
                    
                    {isSuperAdmin && uniqueWebsites.length > 0 && (
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-gray-500" />
                        <select
                          value={selectedWebsiteFilter}
                          onChange={(e) => setSelectedWebsiteFilter(e.target.value)}
                          className="bg-gray-50 border border-gray-200 text-sm rounded-lg px-3 py-1.5 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        >
                          <option value="all">جميع المواقع</option>
                          {uniqueWebsites.map(ws => (
                            <option key={ws} value={ws}>{ws}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  {filteredSessions.length === 0 ? (
                    <div className="p-12 text-center">
                      <MessageSquare className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                      <p className="text-gray-500">لا توجد محادثات متطابقة</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-50">
                      {filteredSessions.map((session) => (
                        <div key={session.session_id} className="transition-colors">
                          <div
                            onClick={() => toggleSession(session.session_id)}
                            className="p-5 hover:bg-gray-50 cursor-pointer flex items-center justify-between"
                          >
                            <div className="flex items-start gap-4 flex-1">
                              <div className="bg-blue-50 rounded-full p-3">
                                <User className="w-5 h-5 text-blue-600" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-1">
                                  <h3 className="font-bold text-gray-900">{session.customer_name}</h3>
                                  {isSuperAdmin && (
                                    <span className="text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded border border-purple-200">
                                      {session.website_id}
                                    </span>
                                  )}
                                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                                    {session.messages_count || 0} رسالة
                                  </span>
                                </div>
                                <div className="flex items-center gap-4 text-sm text-gray-500">
                                  <span className="flex items-center gap-1.5">
                                    <Mail className="w-3.5 h-3.5" />
                                    {session.customer_email}
                                  </span>
                                  <span className="flex items-center gap-1.5">
                                    <Clock className="w-3.5 h-3.5" />
                                    {formatDate(session.started_at)}
                                  </span>
                                </div>
                              </div>
                            </div>
                            {expandedSessions[session.session_id] ? (
                              <ChevronUp className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-400" />
                            )}
                          </div>

                          {/* Conversation Messages */}
                          {expandedSessions[session.session_id] && selectedSession === session.session_id && (
                            <div className="px-5 pb-5 bg-gray-50/50">
                              <div className="bg-white rounded-xl p-5 shadow-inner max-h-96 overflow-y-auto border border-gray-100">
                                {conversations.length === 0 ? (
                                  <p className="text-center text-gray-500 py-4">لا توجد رسائل مسجلة</p>
                                ) : (
                                  <div className="space-y-4">
                                    {conversations.map((msg, idx) => (
                                      <div
                                        key={idx}
                                        className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                                      >
                                        <div
                                          className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                                            msg.sender === 'user'
                                              ? 'bg-blue-600 text-white rounded-tl-sm shadow-sm'
                                              : 'bg-gray-100 text-gray-800 rounded-tr-sm'
                                          }`}
                                        >
                                          <p className="text-sm whitespace-pre-wrap break-words">{msg.message}</p>
                                          <p
                                            className={`text-[10px] mt-1.5 ${
                                              msg.sender === 'user' ? 'text-blue-100' : 'text-gray-500'
                                            }`}
                                          >
                                            {formatTime(msg.timestamp)}
                                          </p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {activeTab === 'users' && isSuperAdmin && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Create User Form */}
                <div className="lg:col-span-1">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                    <h2 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
                      <Plus className="w-5 h-5 text-blue-600" />
                      إضافة عميل جديد
                    </h2>
                    
                    <form onSubmit={handleCreateUser} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">اسم المستخدم للدخول</label>
                        <input
                          type="text"
                          value={newUsername}
                          onChange={(e) => setNewUsername(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                          placeholder="مثال: ahmed"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">كلمة المرور</label>
                        <input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                          placeholder="••••••••"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Website ID (معرف الموقع)</label>
                        <input
                          type="text"
                          value={newWebsiteId}
                          onChange={(e) => setNewWebsiteId(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                          placeholder="مثال: samas_clothing"
                        />
                        <p className="text-xs text-gray-500 mt-1">يجب أن يتطابق مع المعرف في إعدادات إضافة جوملا للعميل.</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">رابط الموقع المسموح به (اختياري)</label>
                        <input
                          type="text"
                          value={newAllowedDomain}
                          onChange={(e) => setNewAllowedDomain(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                          placeholder="مثال: clientdomain.com أو اتركه فارغاً"
                        />
                        <p className="text-xs text-gray-500 mt-1">للحماية: يمنع استخدام Chatbot الخاص بهذا العميل على مواقع أخرى.</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">تعليمات الذكاء الاصطناعي (اختياري)</label>
                        <textarea
                          value={newCustomPrompt}
                          onChange={(e) => setNewCustomPrompt(e.target.value)}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                          placeholder="النص الذي يوجه ردود البوت لهذا العميل (يُحفظ هنا لتجنب طول الرابط في الإضافة)"
                        />
                        <p className="text-xs text-gray-500 mt-1">يُفضّل إدخاله هنا وليس في إعدادات إضافة ووردبريس لتجنب خطأ طول الرابط.</p>
                      </div>

                      {userError && <p className="text-xs text-red-500">{userError}</p>}

                      <button
                        type="submit"
                        className="w-full bg-blue-600 text-white py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors text-sm"
                      >
                        إنشاء الحساب
                      </button>
                    </form>
                  </div>
                </div>

                {/* Users List */}
                <div className="lg:col-span-2">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-5 border-b border-gray-100">
                      <h2 className="text-lg font-bold text-gray-900">إدارة حسابات العملاء</h2>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-right text-sm text-gray-600">
                        <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3">المعرف</th>
                            <th scope="col" className="px-6 py-3">اسم المستخدم</th>
                            <th scope="col" className="px-6 py-3">Website ID</th>
                            <th scope="col" className="px-6 py-3">النطاق المسموح</th>
                            <th scope="col" className="px-6 py-3">تعليمات البوت</th>
                            <th scope="col" className="px-6 py-3">تاريخ الإنشاء</th>
                            <th scope="col" className="px-6 py-3">إجراء</th>
                          </tr>
                        </thead>
                        <tbody>
                          {users.length === 0 ? (
                            <tr>
                              <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                                لا يوجد عملاء. أضف عميلاً جديداً من النموذج على اليسار.
                              </td>
                            </tr>
                          ) : users.map((user) => (
                            <tr key={user.id} className="bg-white border-b hover:bg-gray-50">
                              <td className="px-6 py-4 font-medium">{user.id}</td>
                              <td className="px-6 py-4">
                                {user.username}
                                {user.website_id === 'all' && (
                                  <span className="ml-2 text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded border border-red-200">Super Admin</span>
                                )}
                              </td>
                              <td className="px-6 py-4 text-purple-600 font-mono text-xs">{user.website_id}</td>
                              <td className="px-6 py-4 text-gray-500 font-mono text-xs" dir="ltr">{user.allowed_domain || '*'}</td>
                              <td className="px-6 py-4">
                                {user.custom_prompt && user.custom_prompt.trim() ? (
                                  <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded">نعم</span>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </td>
                              <td className="px-6 py-4">{new Date(user.created_at).toLocaleDateString('ar-EG')}</td>
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => openEditUser(user)}
                                    className="text-blue-600 hover:text-blue-700 p-1 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
                                    title="تعديل المستخدم"
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </button>
                                  {user.username !== currentUser?.username && (
                                    <button
                                      onClick={() => handleDeleteUser(user.id)}
                                      className="text-red-500 hover:text-red-700 p-1 bg-red-50 rounded hover:bg-red-100 transition-colors"
                                      title="حذف المستخدم"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'settings' && !isSuperAdmin && (
              <div className="max-w-3xl">
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <Settings className="w-5 h-5 text-blue-600" />
                    إعدادات البوت لموقعك
                  </h2>
                  <p className="text-sm text-gray-500 mb-6">
                    تحكم في مظهر وسلوك البوت على موقعك. يتم تطبيق هذه الإعدادات تلقائياً على جميع المحادثات.
                  </p>
                  {myPromptLoading ? (
                    <div className="py-8 text-center text-gray-500">جاري التحميل...</div>
                  ) : (
                    <form onSubmit={handleSaveMySettings} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">اسم البوت</label>
                          <input
                            type="text"
                            value={myBotName}
                            onChange={(e) => setMyBotName(e.target.value)}
                            placeholder="Direct Media"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                          />
                          <p className="text-xs text-gray-400 mt-1">يظهر في رأس نافذة المحادثة</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">لون البوت</label>
                          <div className="flex items-center gap-3">
                            <input
                              type="color"
                              value={myBotColor}
                              onChange={(e) => setMyBotColor(e.target.value)}
                              className="w-10 h-10 rounded-lg border border-gray-300 cursor-pointer p-0.5"
                            />
                            <input
                              type="text"
                              value={myBotColor}
                              onChange={(e) => setMyBotColor(e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm font-mono"
                              placeholder="#2563eb"
                            />
                          </div>
                          <p className="text-xs text-gray-400 mt-1">لون الأزرار والرأس والفقاعات</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">عنوان الترحيب</label>
                          <input
                            type="text"
                            value={myWelcomeTitle}
                            onChange={(e) => setMyWelcomeTitle(e.target.value)}
                            placeholder="مرحباً بك!"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                          />
                          <p className="text-xs text-gray-400 mt-1">العنوان الرئيسي في شاشة البدء</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">جملة الترحيب</label>
                          <input
                            type="text"
                            value={myWelcomeSubtitle}
                            onChange={(e) => setMyWelcomeSubtitle(e.target.value)}
                            placeholder="من فضلك ادخل بياناتك للبدء"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                          />
                          <p className="text-xs text-gray-400 mt-1">الجملة تحت عنوان الترحيب</p>
                        </div>
                      </div>

                      <div className="border-t border-gray-200 pt-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">تعليمات البوت (System Prompt)</label>
                        <p className="text-xs text-gray-400 mb-2">هذا النص يوجه ردود البوت في الشات. يتم استخدامه تلقائياً في كل المحادثات.</p>
                        <textarea
                          value={myPrompt}
                          onChange={(e) => setMyPrompt(e.target.value)}
                          rows={10}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm font-mono"
                          placeholder="أدخل تعليمات البوت (نص النظام) هنا..."
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={myPromptSaving}
                        className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-blue-400 transition-colors"
                      >
                        {myPromptSaving ? 'جاري الحفظ...' : 'حفظ الإعدادات'}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            )}

            {/* Edit User Modal */}
            {editUser && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeEditUser}>
                <div
                  className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
                  onClick={(e) => e.stopPropagation()}
                  dir="rtl"
                >
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Pencil className="w-5 h-5 text-blue-600" />
                    تعديل المستخدم
                  </h3>
                  <form onSubmit={handleUpdateUser} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">اسم المستخدم</label>
                      <input
                        type="text"
                        value={editUsername}
                        onChange={(e) => setEditUsername(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                        placeholder="اسم المستخدم"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">كلمة المرور الجديدة (اختياري)</label>
                      <input
                        type="password"
                        value={editPassword}
                        onChange={(e) => setEditPassword(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                        placeholder="اتركه فارغاً للإبقاء على الحالية"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Website ID</label>
                      <input
                        type="text"
                        value={editWebsiteId}
                        onChange={(e) => setEditWebsiteId(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                        placeholder="معرف الموقع"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">رابط الموقع المسموح به (اختياري)</label>
                      <input
                        type="text"
                        value={editAllowedDomain}
                        onChange={(e) => setEditAllowedDomain(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                        placeholder="مثال: clientdomain.com أو *"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">تعليمات الذكاء الاصطناعي (اختياري)</label>
                      <textarea
                        value={editCustomPrompt}
                        onChange={(e) => setEditCustomPrompt(e.target.value)}
                        rows={6}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                        placeholder="النص الذي يوجه ردود البوت لهذا العميل"
                      />
                      <p className="text-xs text-gray-500 mt-1">يُحفظ في اللوحة وليس في الرابط، فلا يسبب خطأ طول الرابط.</p>
                    </div>
                    {editUserError && <p className="text-xs text-red-500">{editUserError}</p>}
                    <div className="flex gap-2 pt-2">
                      <button
                        type="button"
                        onClick={closeEditUser}
                        className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium"
                      >
                        إلغاء
                      </button>
                      <button
                        type="submit"
                        disabled={editLoading}
                        className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-colors text-sm font-medium"
                      >
                        {editLoading ? 'جاري الحفظ...' : 'حفظ التعديلات'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;